import { TrackedChange } from 'models/changes'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

export interface ScrollToContent {
    id: string | null
    correspondingTrackChange: TrackedChange | null
}

export interface PendingScrollRequest {
    scrollToId: string
    correspondingTrackChange: TrackedChange
    timestamp: number
}

interface ScrollStoreState {
    scrollToContent: ScrollToContent
    isHandlingChangeTab: boolean
    pendingScrollRequest: PendingScrollRequest | null
    setScrollToContent: (content: ScrollToContent) => void
    clearScrollToContent: () => void
    setIsHandlingChangeTab: (isHandling: boolean) => void
    setPendingScrollRequest: (request: PendingScrollRequest) => void
    clearPendingScrollRequest: () => void
}

const EMPTY_SCROLL_CONTENT: ScrollToContent = {
    id: null,
    correspondingTrackChange: null,
}

export const useScrollStore = create<ScrollStoreState>((set) => ({
    scrollToContent: {
        id: null,
        correspondingTrackChange: null,
    },
    isHandlingChangeTab: false,
    pendingScrollRequest: null,
    setScrollToContent: (content) =>
        set(() => ({
            scrollToContent: content,
        })),
    clearScrollToContent: () =>
        set(() => ({
            scrollToContent: EMPTY_SCROLL_CONTENT,
        })),
    setIsHandlingChangeTab: (isHandling) =>
        set(() => ({
            isHandlingChangeTab: isHandling,
        })),
    setPendingScrollRequest: (request) =>
        set(() => ({
            pendingScrollRequest: request,
        })),
    clearPendingScrollRequest: () =>
        set(() => ({
            pendingScrollRequest: null,
        })),
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `useScrollStore` store.
 * Sample usage:
 * ```typescript
 * const { scrollToId, setScrollToId } = useScrollStoreShallowSelector((state) => ({
 *   scrollToId: state.scrollToId,
 *   setScrollToId: state.setScrollToId,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useScrollStoreShallowSelector = <T>(
    selector: (state: ScrollStoreState) => T
): T => useScrollStore(useShallow(selector))
